/**
 * Central Permission Constants
 *
 * All permissions used across the application must be defined here.
 * Never use hardcoded permission strings in components.
 *
 * Values MUST match exactly what the backend API returns in login response (user.permissions).
 * Backend uses UPPERCASE_UNDERSCORE format, e.g. "DASHBOARD_VIEW", "PRODUCT_PRE_IPO_INVENTORY_VIEW".
 */

// ==================== ROLE CONSTANTS ====================
export const ROLES = {
  SUPER_ADMIN: 'Super Admin',
  PARTNER: 'Partner',
  INVESTOR: 'Investor',
};

// ==================== DASHBOARD ====================
export const PERMISSIONS = {
  DASHBOARD_VIEW: 'DASHBOARD_VIEW',

  // ==================== INVESTORS ====================
  INVESTORS_VIEW: 'INVESTORS_VIEW',
  INVESTORS_LIST_VIEW: 'INVESTORS_LIST_VIEW',
  INVESTORS_CREATE: 'INVESTORS_CREATE',
  INVESTORS_EDIT: 'INVESTORS_EDIT',
  INVESTORS_DELETE: 'INVESTORS_DELETE',
  INVESTORS_EXPORT: 'INVESTORS_EXPORT',
  INVESTORS_PORTFOLIO_VIEW: 'INVESTORS_PORTFOLIO_VIEW',
  INVESTORS_PERSONAL_INFO_VIEW: 'INVESTORS_PERSONAL_INFO_VIEW',
  INVESTORS_BANK_DETAILS_VIEW: 'INVESTORS_BANK_DETAILS_VIEW',
  INVESTORS_DEMAT_DETAILS_VIEW: 'INVESTORS_DEMAT_DETAILS_VIEW',
  INVESTORS_ADDRESS_VIEW: 'INVESTORS_ADDRESS_VIEW',
  INVESTORS_PROFESSIONAL_VIEW: 'INVESTORS_PROFESSIONAL_VIEW',
  INVESTORS_NOMINEE_VIEW: 'INVESTORS_NOMINEE_VIEW',
  INVESTORS_PRE_IPO_TRANSACTIONS_VIEW: 'INVESTORS_PRE_IPO_TRANSACTIONS_VIEW',
  INVESTORS_PLACE_SELL_ORDER: 'INVESTORS_PLACE_SELL_ORDER',
  INVESTORS_MANUAL_DEAL_SHEET_GENERATE: 'INVESTORS_MANUAL_DEAL_SHEET_GENERATE',
  INVESTOR_BASIC_PROFILE_EDIT: 'INVESTOR_BASIC_PROFILE_EDIT',
  RA_REFERRAL_VIEW: 'RA_REFERRAL_VIEW',

  // ==================== BANK ACCOUNTS ====================
  BANK_SET_PRIMARY: 'BANK_SET_PRIMARY',
  DEMAT_NUMBER_EDIT: 'DEMAT_NUMBER_EDIT',
  DEMAT_OCR_VERIFY: 'DEMAT_OCR_VERIFY',

  // ==================== PRODUCT MASTER ====================
  PRODUCT_MASTER_VIEW: 'PRODUCT_MASTER_VIEW',

  // Fixed Deposit
  PRODUCT_FIXED_DEPOSIT_VIEW: 'PRODUCT_FIXED_DEPOSIT_VIEW',
  PRODUCT_FIXED_DEPOSIT_CREATE: 'PRODUCT_FIXED_DEPOSIT_CREATE',
  PRODUCT_FIXED_DEPOSIT_EDIT: 'PRODUCT_FIXED_DEPOSIT_EDIT',
  PRODUCT_FIXED_DEPOSIT_DELETE: 'PRODUCT_FIXED_DEPOSIT_DELETE',

  // Pre IPO
  PRODUCT_PRE_IPO_VIEW: 'PRODUCT_PRE_IPO_VIEW',
  PRODUCT_PRE_IPO_REPORTING_VIEW: 'PRODUCT_PRE_IPO_REPORTING_VIEW',
  PRODUCT_PRE_IPO_INVENTORY_VIEW: 'PRODUCT_PRE_IPO_INVENTORY_VIEW',
  PRODUCT_PRE_IPO_INVENTORY_EDIT: 'PRODUCT_PRE_IPO_INVENTORY_EDIT',
  PRODUCT_PRE_IPO_REAL_INVENTORY_VIEW: 'PRODUCT_PRE_IPO_REAL_INVENTORY_VIEW',
  PRODUCT_PRE_IPO_REAL_INVENTORY_EDIT: 'PRODUCT_PRE_IPO_REAL_INVENTORY_EDIT',
  PRODUCT_PRE_IPO_LISTINGS_VIEW: 'PRODUCT_PRE_IPO_LISTINGS_VIEW',
  PRODUCT_PRE_IPO_LISTINGS_CREATE: 'PRODUCT_PRE_IPO_LISTINGS_CREATE',
  PRODUCT_PRE_IPO_ORDER_MANAGEMENT_VIEW: 'PRODUCT_PRE_IPO_ORDER_MANAGEMENT_VIEW',
  PRODUCT_PRE_IPO_NEW_SHARES_REQUEST_VIEW: 'PRODUCT_PRE_IPO_NEW_SHARES_REQUEST_VIEW',
  PRODUCT_PRE_IPO_NEW_SELL_REQUEST_VIEW: 'PRODUCT_PRE_IPO_NEW_SELL_REQUEST_VIEW',
  PRODUCT_PRE_IPO_PURCHASE_DEAL_SHEET_VIEW: 'PRODUCT_PRE_IPO_PURCHASE_DEAL_SHEET_VIEW',
  PRODUCT_PRE_IPO_PURCHASE_DEAL_SHEET_MANAGE: 'PRODUCT_PRE_IPO_PURCHASE_DEAL_SHEET_MANAGE',
  PRODUCT_PRE_IPO_TRADE_COMMITMENT_VIEW: 'PRODUCT_PRE_IPO_TRADE_COMMITMENT_VIEW',
  PRODUCT_PRE_IPO_TRADE_COMMITMENT_MANAGE: 'PRODUCT_PRE_IPO_TRADE_COMMITMENT_MANAGE',
  PRODUCT_PRE_IPO_SELLER_DEAL_SHEET_VIEW: 'PRODUCT_PRE_IPO_SELLER_DEAL_SHEET_VIEW',
  PRODUCT_PRE_IPO_OVERVIEW_DETAILS_VIEW: 'PRODUCT_PRE_IPO_OVERVIEW_DETAILS_VIEW',
  PRODUCT_PRE_IPO_ATTRIBUTE_CATALOG_VIEW: 'PRODUCT_PRE_IPO_ATTRIBUTE_CATALOG_VIEW',

  // Attribute Catalog (PRODUCTS_* for CRUD actions)
  PRODUCTS_VIEW: 'PRODUCTS_VIEW',
  PRODUCTS_CREATE: 'PRODUCTS_CREATE',
  PRODUCTS_EDIT: 'PRODUCTS_EDIT',
  PRODUCTS_DELETE: 'PRODUCTS_DELETE',

  // Pre-IPO orders (API: GET/PATCH /admin/orders)
  ORDERS_VIEW: 'ORDERS_VIEW',
  ORDERS_MANAGE: 'ORDERS_MANAGE',
  VIEW_ORDERS_ALTCASE_PRICE: 'VIEW_ORDERS_ALTCASE_PRICE',
  VIEW_ORDER_SUPPLIER: 'VIEW_ORDER_SUPPLIER',
  ORDERS_CHANGE_PARTNER_PRICE: 'ORDERS_CHANGE_PARTNER_PRICE',
  ORDERS_QUANTITY_EDIT: 'ORDERS_QUANTITY_EDIT',
  ORDERS_PARTNER_PRICE_EDIT: 'ORDERS_PARTNER_PRICE_EDIT',
  RECONCILE_ORDER: 'RECONCILE_ORDER',
  EXPORT_ORDER_DATA: 'EXPORT_ORDER_DATA',
  ORDERS_DUPLICATE_OFFER_EXPIRED: 'ORDERS_DUPLICATE_OFFER_EXPIRED',
  ORDERS_REPORT_EXCLUSION_MANAGE: 'ORDERS_REPORT_EXCLUSION_MANAGE',
  INVENTORY_VIEW: 'INVENTORY_VIEW',
  INVENTORY_MANAGE: 'INVENTORY_MANAGE',

  // Basket Listings
  PRODUCT_BASKET_LISTINGS_VIEW: 'PRODUCT_BASKET_LISTINGS_VIEW',
  PRODUCT_BASKET_LISTINGS_CREATE: 'PRODUCT_BASKET_LISTINGS_CREATE',
  PRODUCT_BASKET_LISTINGS_EDIT: 'PRODUCT_BASKET_LISTINGS_EDIT',

  // Masters
  PRODUCT_MASTERS_VIEW: 'PRODUCT_MASTERS_VIEW',
  PRODUCT_SUPPLIER_MASTERS_VIEW: 'PRODUCT_SUPPLIER_MASTERS_VIEW',
  PRODUCT_SUPPLIER_MASTERS_CREATE: 'PRODUCT_SUPPLIER_MASTERS_CREATE',
  PRODUCT_SUPPLIER_MASTERS_EDIT: 'PRODUCT_SUPPLIER_MASTERS_EDIT',
  SUPPLIERS_VIEW: 'SUPPLIERS_VIEW',
  SUPPLIERS_MANAGE: 'SUPPLIERS_MANAGE',
  SUPPLIERS_PROFILE_BANK_ADD: 'SUPPLIERS_PROFILE_BANK_ADD',
  SUPPLIERS_PROFILE_DEMAT_ADD: 'SUPPLIERS_PROFILE_DEMAT_ADD',
  SUPPLIERS_PROFILE_BANK_SET_PRIMARY: 'SUPPLIERS_PROFILE_BANK_SET_PRIMARY',

  // ==================== PARTNERS ====================
  PARTNER_VIEW: 'PARTNER_VIEW',
  PARTNER_LIST_VIEW: 'PARTNER_LIST_VIEW',
  PARTNER_CREATE: 'PARTNER_CREATE',
  PARTNER_EDIT: 'PARTNER_EDIT',
  PARTNER_MARGIN_EDIT: 'PARTNER_MARGIN_EDIT',
  PARTNER_DELETE: 'PARTNER_DELETE',
  PARTNER_ASSIGN_PRICING_GROUP: 'PARTNER_ASSIGN_PRICING_GROUP',
  PARTNER_BASIC_PROFILE_EDIT: 'PARTNER_BASIC_PROFILE_EDIT',

  // ==================== PRICING ====================
  PRICING_VIEW: 'PRICING_VIEW',
  PRICING_GROUPS_VIEW: 'PRICING_GROUPS_VIEW',
  PRICING_GROUPS_CREATE: 'PRICING_GROUPS_CREATE',
  PRICING_GROUPS_EDIT: 'PRICING_GROUPS_EDIT',
  PRICING_SPECIAL_TIERS_VIEW: 'PRICING_SPECIAL_TIERS_VIEW',
  PRICING_SPECIAL_TIERS_CREATE: 'PRICING_SPECIAL_TIERS_CREATE',
  PRICING_SPECIAL_TIERS_EDIT: 'PRICING_SPECIAL_TIERS_EDIT',
  PRICING_SPECIAL_PRODUCT_TIERS_VIEW: 'PRICING_SPECIAL_PRODUCT_TIERS_VIEW',
  PRICING_SPECIAL_PRODUCT_TIERS_CREATE: 'PRICING_SPECIAL_PRODUCT_TIERS_CREATE',
  PRICING_SPECIAL_PRODUCT_TIERS_EDIT: 'PRICING_SPECIAL_PRODUCT_TIERS_EDIT',
  PRICING_SPECIAL_PRICING_REQUESTS_VIEW: 'PRICING_SPECIAL_PRICING_REQUESTS_VIEW',
  PRICING_SPECIAL_PRICING_REQUESTS_CREATE: 'PRICING_SPECIAL_PRICING_REQUESTS_CREATE',
  PRICING_SPECIAL_PRICING_REQUESTS_APPROVE: 'PRICING_SPECIAL_PRICING_REQUESTS_APPROVE',
  PRICING_PUBLISH_VIEW: 'PRICING_PUBLISH_VIEW',
  PRICING_PUBLISH_ACTION: 'PRICING_PUBLISH_ACTION',
  PRICING_BUSINESS_ALERTS_VIEW: 'PRICING_BUSINESS_ALERTS_VIEW',
  PRICING_BUSINESS_ALERTS_CREATE: 'PRICING_BUSINESS_ALERTS_CREATE',
  PRICING_BUSINESS_ALERTS_EDIT: 'PRICING_BUSINESS_ALERTS_EDIT',
  PRICING_WHATSAPP_GROUPS_VIEW: 'PRICING_WHATSAPP_GROUPS_VIEW',
  PRICING_WHATSAPP_GROUPS_CREATE: 'PRICING_WHATSAPP_GROUPS_CREATE',
  PRICING_WHATSAPP_GROUPS_EDIT: 'PRICING_WHATSAPP_GROUPS_EDIT',
  PRICING_WHATSAPP_CHATBOT_VIEW: 'PRICING_WHATSAPP_CHATBOT_VIEW',
  PRICING_WHATSAPP_CHATBOT_CREATE: 'PRICING_WHATSAPP_CHATBOT_CREATE',
  PRICING_WHATSAPP_CHATBOT_EDIT: 'PRICING_WHATSAPP_CHATBOT_EDIT',
  PRICING_SLAB_MATRIX_VIEW: 'PRICING_SLAB_MATRIX_VIEW',
  PRICING_SLAB_MATRIX_CREATE: 'PRICING_SLAB_MATRIX_CREATE',
  PRICING_SLAB_MATRIX_EDIT: 'PRICING_SLAB_MATRIX_EDIT',

  // ==================== PRICE CHART ====================
  PRICE_CHART_VIEW: 'PRICE_CHART_VIEW',
  PRICE_CHART_EXPORT: 'PRICE_CHART_EXPORT',

  // ==================== NEWS ====================
  NEWS_VIEW: 'NEWS_VIEW',
  NEWS_CREATE: 'NEWS_CREATE',
  NEWS_EDIT: 'NEWS_EDIT',
  NEWS_DELETE: 'NEWS_DELETE',
  NEWS_PUBLISH: 'NEWS_PUBLISH',

  // ==================== COMMISSIONS ====================
  COMMISSIONS_VIEW: 'COMMISSIONS_VIEW',
  COMMISSIONS_EDIT: 'COMMISSIONS_EDIT',
  COMMISSIONS_APPROVE: 'COMMISSIONS_APPROVE',

  // ==================== INTRODUCER ====================
  INTRODUCER_VIEW: 'INTRODUCER_VIEW',
  INTRODUCER_CREATE: 'INTRODUCER_CREATE',
  INTRODUCER_EDIT: 'INTRODUCER_EDIT',
  INTRODUCER_DELETE: 'INTRODUCER_DELETE',

  // ==================== RBAC ====================
  RBAC_VIEW: 'RBAC_VIEW',
  RBAC_USER_MANAGEMENT_VIEW: 'RBAC_USER_MANAGEMENT_VIEW',
  RBAC_USER_MANAGEMENT_CREATE: 'RBAC_USER_MANAGEMENT_CREATE',
  RBAC_USER_MANAGEMENT_EDIT: 'RBAC_USER_MANAGEMENT_EDIT',
  RBAC_USER_MANAGEMENT_DELETE: 'RBAC_USER_MANAGEMENT_DELETE',
  RBAC_ROLES_PERMISSIONS_VIEW: 'RBAC_ROLES_PERMISSIONS_VIEW',
  RBAC_ROLES_PERMISSIONS_EDIT: 'RBAC_ROLES_PERMISSIONS_EDIT',
  RBAC_RESET_PASSWORD_VIEW: 'RBAC_RESET_PASSWORD_VIEW',
  RBAC_RESET_PASSWORD_ACTION: 'RBAC_RESET_PASSWORD_ACTION',
  RBAC_HIERARCHY_VIEW: 'RBAC_HIERARCHY_VIEW',
  RBAC_HIERARCHY_EDIT: 'RBAC_HIERARCHY_EDIT',
  RBAC_AUDIT_VIEW: 'RBAC_AUDIT_VIEW',
  MANAGE_ROLES: 'MANAGE_ROLES',
  MANAGE_PERMISSIONS: 'MANAGE_PERMISSIONS',
  VIEW_SENSITIVE_DATA: 'VIEW_SENSITIVE_DATA',
  VIEW_FULL_INTERNAL_HIERARCHY: 'VIEW_FULL_INTERNAL_HIERARCHY',

  // ==================== SETTINGS / INTEGRATIONS ====================
  SETTINGS_VIEW: 'SETTINGS_VIEW',
  INTEGRATION_WHATSAPP_VIEW: 'INTEGRATION_WHATSAPP_VIEW',
  INTEGRATION_WHATSAPP_MANAGE: 'INTEGRATION_WHATSAPP_MANAGE',

  // ==================== ADMIN NOTIFICATIONS ====================
  ADMIN_NOTIFICATIONS_VIEW: 'ADMIN_NOTIFICATIONS_VIEW',
  ADMIN_NOTIFICATIONS_MANAGE: 'ADMIN_NOTIFICATIONS_MANAGE',

  // ==================== CONFIGURATION ====================
  CONFIGURATION_VIEW: 'CONFIGURATION_VIEW',

  // ==================== BANNERS ====================
  BANNERS_VIEW: 'BANNERS_VIEW',
  BANNERS_CREATE: 'BANNERS_CREATE',
  BANNERS_EDIT: 'BANNERS_EDIT',
  BANNERS_DELETE: 'BANNERS_DELETE',

  // ==================== SALES REPORTING ====================
  SALES_RM_TARGETS_VIEW: 'SALES_RM_TARGETS_VIEW',
  SALES_RM_TARGETS_MANAGE: 'SALES_RM_TARGETS_MANAGE',
  SALES_RM_QUARTER_TARGETS_VIEW: 'SALES_RM_QUARTER_TARGETS_VIEW',
  SALES_RM_QUARTER_TARGETS_MANAGE: 'SALES_RM_QUARTER_TARGETS_MANAGE',
  MTD_BUSINESS_REPORT_VIEW: 'MTD_BUSINESS_REPORT_VIEW',
  MTD_BUSINESS_REPORT_SEND: 'MTD_BUSINESS_REPORT_SEND',
  SALES_PERFORMANCE_REPORT_VIEW: 'SALES_PERFORMANCE_REPORT_VIEW',
  SALES_PERFORMANCE_REPORT_SEND: 'SALES_PERFORMANCE_REPORT_SEND',
  YTD_MIS_REPORT_VIEW: 'YTD_MIS_REPORT_VIEW',
  ORDER_WISE_REVENUE_EXPORT: 'ORDER_WISE_REVENUE_EXPORT',
  REPORT_EMAIL_RECIPIENTS_VIEW: 'REPORT_EMAIL_RECIPIENTS_VIEW',
  REPORT_EMAIL_RECIPIENTS_MANAGE: 'REPORT_EMAIL_RECIPIENTS_MANAGE',
  OFFLINE_PRODUCT_SALES_VIEW: 'OFFLINE_PRODUCT_SALES_VIEW',
  OFFLINE_PRODUCT_SALES_MANAGE: 'OFFLINE_PRODUCT_SALES_MANAGE',
};

/**
 * Permission Groups - useful for checking multiple related permissions
 */
export const PERMISSION_GROUPS = {
  INVESTORS_FULL: [
    PERMISSIONS.INVESTORS_VIEW,
    PERMISSIONS.INVESTORS_LIST_VIEW,
    PERMISSIONS.INVESTORS_CREATE,
    PERMISSIONS.INVESTORS_EDIT,
    PERMISSIONS.INVESTORS_DELETE,
    PERMISSIONS.INVESTORS_PORTFOLIO_VIEW,
    PERMISSIONS.INVESTORS_PERSONAL_INFO_VIEW,
    PERMISSIONS.INVESTORS_BANK_DETAILS_VIEW,
    PERMISSIONS.INVESTORS_DEMAT_DETAILS_VIEW,
    PERMISSIONS.INVESTORS_ADDRESS_VIEW,
    PERMISSIONS.INVESTORS_PROFESSIONAL_VIEW,
    PERMISSIONS.INVESTORS_NOMINEE_VIEW,
    PERMISSIONS.INVESTORS_PRE_IPO_TRANSACTIONS_VIEW,
    PERMISSIONS.INVESTORS_PLACE_SELL_ORDER,
  ],

  PRODUCT_MASTER_FULL: [
    PERMISSIONS.PRODUCT_MASTER_VIEW,
    PERMISSIONS.PRODUCT_FIXED_DEPOSIT_VIEW,
    PERMISSIONS.PRODUCT_PRE_IPO_VIEW,
    PERMISSIONS.PRODUCT_BASKET_LISTINGS_VIEW,
    PERMISSIONS.PRODUCT_MASTERS_VIEW,
  ],

  RBAC_FULL: [
    PERMISSIONS.RBAC_VIEW,
    PERMISSIONS.RBAC_USER_MANAGEMENT_VIEW,
    PERMISSIONS.RBAC_ROLES_PERMISSIONS_VIEW,
    PERMISSIONS.RBAC_RESET_PASSWORD_VIEW,
    PERMISSIONS.RBAC_HIERARCHY_VIEW,
  ],
};

export default PERMISSIONS;
